import UploadCircleIcon from '../../../assets/svg/upload-circle.svg?react';
import FJLocalStore from '../../../store/FJLocalStore';
import styles from './FJImageUploadWidthHistory.module.less';
import { useRef, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { UPLOAD_FILE_TYPE_NO_GIF } from '../../../config/dataConfig/FJGenerateData';
import classNames from 'classnames';
import FJConstant from '../../../constants/FJConstant';
import UploadBorderIcon from '../../../assets/svg/upload-border.svg?react';
import UploadBorderHoverIcon from '../../../assets/svg/upload-border-hover.svg?react';
import FJUploadGradientLineIcon from '../../../assets/svg/upload-gradient-line.svg?react';
import FJUtil from '../../../util/FJUtil';
import FJLoading from '../../components/FJLoading/FJLoading.jsx';
import FJImageUpload from '../../../common/components/FJUpload/FJImageUpload/FJImageUpload.jsx';
import FJImageInput from '../../components/FJImageInput/FJImageInput.jsx';

const EmptyComponent = props => {
    const currentTag = FJLocalStore.currentTag();
    const timer = useRef(null);
    const { children, onClick, isOptional, isSmallComponent, text } = props;
    const uploadRef = useRef(null);

    useEffect(() => {
        timer.current = setTimeout(() => {
            const spanDom = uploadRef.current?.getElementsByTagName('span')[0];
            if (spanDom) {
                spanDom.addEventListener('click', e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClick();
                });
            }
        }, 500);
        return () => {
            timer.current && clearTimeout(timer.current);
        };
    }, [uploadRef.current, currentTag, onClick]);

    return (
        <>
            {isSmallComponent ? (
                <div className={styles.small_upload_box}>
                    <UploadCircleIcon className={styles.upload_icon} />
                    <div
                        className={styles.upload_text}
                        dangerouslySetInnerHTML={{
                            __html: FJLocalStore._('upload-or-use-ai-image'),
                        }}
                        ref={uploadRef}
                    />
                </div>
            ) : (
                <div className={styles.big_upload_box}>
                    <div className={styles.big_upload_text}>
                        <FJUploadGradientLineIcon /> <span>{FJLocalStore._(text ?? 'upload')}</span>
                    </div>
                    <div
                        className={styles.upload_text}
                        dangerouslySetInnerHTML={{
                            __html: FJLocalStore._('or-from-AI-image'),
                        }}
                        ref={uploadRef}
                    />
                    {isOptional && <div className={styles.optional_text}>{FJLocalStore._('optional')}</div>}
                </div>
            )}
            {children}
        </>
    );
};

const FJImageUploadWidthHistory = props => {
    const {
        uploadImg,
        uploadStatus = FJConstant.DEFAULT,
        isSmallComponent = false,
        originImage = {},
        showAIImageHistoryList,
        onDelete,
        fileType,
        openMediaLibrary,
        style,
        isOptional,
        label,
        text,
        hasCrop,
        onCrop,
        previewImageStyle,
    } = props;
    const inputRef = useRef(null);
    const [isDraggingFile, setIsDraggingFile] = useState(false);
    const _onUploadImg = e => {
        const file = e.target.files[0];
        uploadImg(file);
        e.target.value = null;
    };
    const _onUpload = async e => {
        e.stopPropagation();
        if (!FJUtil.isLogin()) {
            FJUtil.login();
            return;
        }
        if (uploadStatus === FJConstant.LOADING) {
            return;
        }
        if (window.isEditPage && openMediaLibrary) {
            openMediaLibrary();
        } else if (inputRef.current) {
            inputRef.current.click();
        }
    };
    const _onDragOver = e => {
        setIsDraggingFile(true);
        e.stopPropagation();
        e.preventDefault();
    };

    const _onDragLeave = e => {
        setIsDraggingFile(false);
        e.stopPropagation();
        e.preventDefault();
    };

    const _onDragDrop = e => {
        setIsDraggingFile(false);
        e.stopPropagation();
        e.preventDefault();
        let files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            uploadImg(file);
        }
    };
    const _onDelete = () => {
        onDelete();
    };

    const renderEmptyUpload = () => {
        return (
            <EmptyComponent
                onClick={showAIImageHistoryList}
                isOptional={isOptional}
                isSmallComponent={isSmallComponent}
                text={text}
            >
                {renderBorder()}
            </EmptyComponent>
        );
    };

    const renderBorder = () => {
        return (
            <div className={styles.upload_border_box}>
                <UploadBorderIcon className={styles.upload_border} />
                <UploadBorderHoverIcon className={styles.upload_border_hover} />
            </div>
        );
    };
    const renderSuccess = () => {
        return isSmallComponent ? (
            <FJImageInput
                uploadImageInfo={originImage}
                isDelete={true}
                isDraggingFile={isDraggingFile}
                onDelete={_onDelete}
                acceptFile={fileType || UPLOAD_FILE_TYPE_NO_GIF}
                openMediaLibrary={openMediaLibrary}
                setIsDraggingFile={setIsDraggingFile}
                uploadImg={uploadImg}
                style={{ height: 60 }}
                previewClassName={styles.preview_image}
                previewImageStyle={previewImageStyle}
            />
        ) : (
            <FJImageUpload
                imageInfo={originImage}
                isDelete={true}
                isDraggingFile={isDraggingFile}
                onDelete={_onDelete}
                acceptFile={fileType || UPLOAD_FILE_TYPE_NO_GIF}
                openMediaLibrary={openMediaLibrary}
                setIsDraggingFile={setIsDraggingFile}
                uploadImg={uploadImg}
                style={{ height: 154, width: 204 }}
                label={label}
                previewClassName={styles.preview_image}
                previewImageStyle={previewImageStyle}
            />
        );
    };

    const renderGenerating = () => {
        return (
            <>
                <div className={styles.preview_box}>
                    <div className={styles.loading}>
                        <FJLoading strokeColor='#A8ACBC' />
                    </div>
                </div>
                {renderBorder()}
            </>
        );
    };
    const renderMain = () => {
        switch (uploadStatus) {
            case FJConstant.DEFAULT:
                return renderEmptyUpload();
            case FJConstant.SUCCESS:
                return renderSuccess();
            default:
                return renderGenerating();
        }
    };

    return (
        <div
            className={classNames(
                styles.box,
                isDraggingFile && styles.dragging,
                isSmallComponent && styles.small,
                uploadStatus === FJConstant.SUCCESS && styles.success,
            )}
            onDragOver={_onDragOver}
            onDrop={_onDragDrop}
            onDragLeave={_onDragLeave}
            onClick={uploadStatus === FJConstant.SUCCESS ? null : _onUpload}
            style={style}
        >
            {renderMain()}
            <input
                type='file'
                ref={inputRef}
                accept={fileType || UPLOAD_FILE_TYPE_NO_GIF}
                className={styles.input}
                onChange={_onUploadImg}
            />
        </div>
    );
};

FJImageUploadWidthHistory.propTypes = {
    openMediaLibrary: PropTypes.func,
    uploadImg: PropTypes.func,
    isLoading: PropTypes.bool,
    isUploading: PropTypes.bool,
    uploadStatus: PropTypes.string,
    isSmallComponent: PropTypes.bool,
    originImage: PropTypes.object,
    setIsDraggingFile: PropTypes.func,
    style: PropTypes.object,
};

export default FJImageUploadWidthHistory;
